import React from 'react';
import styled from 'styled-components/macro';
import { Typography, Button } from '@deeptrue-frontend/ui';
import { useIntl } from 'react-intl';
import showdown from 'showdown';
import { ReactComponent as RecIcon } from 'assets/icons/recommendation.svg';
import { ReactComponent as WarnIcon } from 'assets/icons/warning_reco.svg';
import { ReactComponent as BulbIcon } from 'assets/icons/bulb.svg';
import { useFormatMessage } from 'components/IntlProvider';
import { ReactComponent as NeutralIcon } from 'assets/icons/neutral.svg';
import { ReactComponent as GoodIcon } from 'assets/icons/good.svg';
import { InterpretationType } from '../../../types';
import { translateGroupName } from '../../../utils';
import { Tabs } from '../../../DefaultReports';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e5e8;
  background-color: white;
  border-radius: 4px;
  padding: 2rem 4rem;
  margin: 1rem 0;
  position: relative;
`;

const Box = styled.div`
  width: 45%;
  display: flex;
  flex-direction: column;

  &:first-of-type {
    margin-right: 1rem;
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
    margin-left: 1rem;
  }
`;

const BoxWrapper = styled.div`
  display: flex;
  margin-bottom: 2rem;
  justify-content: space-between;
`;

const RecommendationTitle = styled(Typography)`
  position: relative;
  font-size: 1.125rem;
  font-weight: 500;
`;

const IconWrapper = styled.span`
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  margin-top: -0.1rem;
`;

const Title = styled(RecommendationTitle)`
  position: relative;
  font-weight: 500;
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const Btn = styled(Button)`
  border: 1px solid #e3e5e8;
`;

const MarkDownWrapper = styled.span`
  p {
    margin: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  margin-left: -2.3rem;
`;

const renderIcon = (icon: string) => {
  switch (icon) {
    case 'warn':
      return <WarnIcon />;
    case 'neutral':
      return <NeutralIcon />;
    case 'good':
      return <GoodIcon />;
    case 'bulb':
      return <BulbIcon />;
    default:
      return <WarnIcon />;
  }
};

interface Props extends InterpretationType {
  changeTab: (tab: Tabs) => void;
  disableGoToResults?: boolean;
}

const Interpretation = ({
  title,
  interpretationText,
  recommendationText,
  isRecommendation,
  icon,
  changeTab,
  disableGoToResults,
}: Props) => {
  const t = useFormatMessage();
  const { locale } = useIntl();
  const converter = new showdown.Converter();

  const titleWithMarkDown = converter.makeHtml(
    translateGroupName(title, locale)
  );
  const interpretationWithMarkdown = converter.makeHtml(
    translateGroupName(interpretationText, locale)
  );

  const recommendationTextWithMarkdown = converter.makeHtml(
    translateGroupName(recommendationText, locale)
  );

  return (
    <Content>
      <BoxWrapper>
        <Box>
          <TitleWrapper>
            <IconWrapper>{renderIcon(icon)}</IconWrapper>

            <Title>
              <MarkDownWrapper
                dangerouslySetInnerHTML={{ __html: titleWithMarkDown }}
              />
            </Title>
          </TitleWrapper>
          <Typography fontWeight="400" fontSize="0.875rem">
            <MarkDownWrapper
              dangerouslySetInnerHTML={{ __html: interpretationWithMarkdown }}
            />
          </Typography>
        </Box>
        <Box>
          {isRecommendation && (
            <>
              <TitleWrapper>
                <IconWrapper>
                  <RecIcon />
                </IconWrapper>
                <RecommendationTitle fontSize="1rem" fontWeight="500">
                  {t('report.insights.recommendation', 'Recommendation')}
                </RecommendationTitle>
              </TitleWrapper>

              <Typography fontWeight="400" fontSize="0.875rem">
                <MarkDownWrapper
                  dangerouslySetInnerHTML={{
                    __html: recommendationTextWithMarkdown,
                  }}
                />
              </Typography>
            </>
          )}
        </Box>
      </BoxWrapper>
      {!disableGoToResults && (
        <Btn
          variant="outlined"
          width="10rem"
          onClick={() => changeTab(Tabs.results)}
        >
          {t('report.insights.goToResults', 'Go to results')}
        </Btn>
      )}
    </Content>
  );
};

export default Interpretation;
